import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Upload, CheckCircle2, Loader2, Users, Wallet, ClipboardCheck } from "lucide-react";
import { fileToDataUrl } from "@/lib/sales";

// CRM Onboarding — step between Sales-Won and Design.
// Captures Ops team + 5%/45% payment details + hand-off artifacts.
// Only rendered for CRM Manager / CRM Executive (or Admin/Director).
// On save: amount_received += 5% + 45%, is_45_percent_paid=true → client dashboard unlocks.
export default function CrmOnboardingPanel({ project, canEdit, onChanged }) {
  const [opsExecs, setOpsExecs] = useState([]);
  const [opsMgrs, setOpsMgrs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    operations_executive_id: project.operations_executive_id || "",
    operations_manager_id: project.operations_manager_id || "",
    amount_5_percent: project.amount_5_percent ?? "",
    amount_45_percent: project.amount_45_percent ?? "",
    payment_5_date: project.payment_5_date || "",
    payment_45_date: project.payment_45_date || "",
    final_2d: "",
    final_3d: "",
    quotation: "",
    signoff_sheet: "",
    notes: project.crm_notes || "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [ex, mg] = await Promise.all([
          api.get("/users/by-role/Operations Executive").catch(() => ({ data: [] })),
          api.get("/users/by-role/Operations Manager").catch(() => ({ data: [] })),
        ]);
        setOpsExecs(ex.data || []);
        setOpsMgrs(mg.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const onFile = async (key, files) => {
    if (!files?.[0]) return;
    setField(key, await fileToDataUrl(files[0]));
  };

  const done = !!project.crm_onboarded_at;
  const existingFiles = project.crm_files || {};

  const submit = async (e) => {
    e.preventDefault();
    if (form.amount_5_percent === "" || form.amount_45_percent === "") {
      setErr("Both 5% and 45% payment amounts are required");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      const payload = {
        operations_executive_id: form.operations_executive_id || null,
        operations_manager_id: form.operations_manager_id || null,
        amount_5_percent: Number(form.amount_5_percent),
        amount_45_percent: Number(form.amount_45_percent),
        payment_5_date: form.payment_5_date || null,
        payment_45_date: form.payment_45_date || null,
        final_2d: form.final_2d || undefined,
        final_3d: form.final_3d || undefined,
        quotation: form.quotation || undefined,
        signoff_sheet: form.signoff_sheet || undefined,
        notes: form.notes || null,
      };
      await api.post(`/projects/${project.id}/crm/onboard`, payload);
      toast.success("CRM onboarding saved · Client dashboard unlocked");
      onChanged?.();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <section className="iz-card p-6" data-testid="crm-onboarding-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">CRM Onboarding</p>
          <h3 className="font-display text-xl text-white flex items-center gap-2">
            {done && <CheckCircle2 className="w-4 h-4 text-emerald-400"/>}
            Client Handoff · 5% + 45% Milestone
          </h3>
          <p className="text-[11px] text-gray-500 font-mono">
            {done ? "Completed — 45% received, client dashboard unlocked. Values below can be corrected."
                  : "Capture Ops team + payments + hand-off files. Saving unlocks the 13-point client timeline."}
          </p>
        </div>
        {done && (
          <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-400" data-testid="crm-onboard-status">
            Unlocked
          </span>
        )}
      </div>

      <form onSubmit={submit} className="space-y-5">
        {/* Ops team */}
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2 flex items-center gap-1.5">
            <Users className="w-3 h-3"/> Operations Team
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SelectField label="Operations Manager" value={form.operations_manager_id}
                         onChange={v => setField("operations_manager_id", v)} options={opsMgrs}
                         disabled={!canEdit} testid="crm-ops-manager"/>
            <SelectField label="Operations Executive" value={form.operations_executive_id}
                         onChange={v => setField("operations_executive_id", v)} options={opsExecs}
                         disabled={!canEdit} testid="crm-ops-exec"/>
          </div>
        </div>

        {/* Payments */}
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2 flex items-center gap-1.5">
            <Wallet className="w-3 h-3"/> Milestone Payments
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <TextField label="5% Amount (₹)" value={form.amount_5_percent} onChange={v => setField("amount_5_percent", v)} type="number" disabled={!canEdit} testid="crm-amt-5"/>
            <TextField label="5% Payment Date" value={form.payment_5_date} onChange={v => setField("payment_5_date", v)} type="date" disabled={!canEdit} testid="crm-date-5"/>
            <TextField label="45% Amount (₹)" value={form.amount_45_percent} onChange={v => setField("amount_45_percent", v)} type="number" disabled={!canEdit} testid="crm-amt-45"/>
            <TextField label="45% Payment Date" value={form.payment_45_date} onChange={v => setField("payment_45_date", v)} type="date" disabled={!canEdit} testid="crm-date-45"/>
          </div>
        </div>

        {/* Files */}
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2 flex items-center gap-1.5">
            <ClipboardCheck className="w-3 h-3"/> Hand-off Artifacts
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <CrmFileSlot label="Final 2D"        keyName="final_2d"      value={form.final_2d}      existing={existingFiles.final_2d}      onChange={onFile} disabled={!canEdit}/>
            <CrmFileSlot label="Final 3D"        keyName="final_3d"      value={form.final_3d}      existing={existingFiles.final_3d}      onChange={onFile} disabled={!canEdit}/>
            <CrmFileSlot label="Quotation"       keyName="quotation"     value={form.quotation}     existing={existingFiles.quotation}     onChange={onFile} disabled={!canEdit}/>
            <CrmFileSlot label="Sign-off Sheet"  keyName="signoff_sheet" value={form.signoff_sheet} existing={existingFiles.signoff_sheet} onChange={onFile} disabled={!canEdit}/>
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Notes</label>
          <textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} className="iz-input" rows={2} disabled={!canEdit} data-testid="crm-notes"/>
        </div>

        {err && <p className="text-xs text-red-400" data-testid="crm-onboard-error">{err}</p>}

        {canEdit && (
          <div className="flex justify-end">
            <button type="submit" disabled={saving} className="iz-btn-primary flex items-center gap-2" data-testid="crm-onboard-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <CheckCircle2 className="w-3.5 h-3.5"/>}
              {saving ? "Saving…" : done ? "Save Changes" : "Complete Onboarding · Unlock Client"}
            </button>
          </div>
        )}
      </form>
    </section>
  );
}

function TextField({ label, value, onChange, type = "text", disabled, testid }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="iz-input" disabled={disabled} data-testid={testid}/>
    </div>
  );
}

function SelectField({ label, value, onChange, options, disabled, testid }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">{label}</label>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="iz-input" disabled={disabled} data-testid={testid}>
        <option value="">— Not assigned —</option>
        {options.map(u => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
      </select>
    </div>
  );
}

function CrmFileSlot({ label, keyName, value, existing, onChange, disabled }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">{label}</label>
      <label className={`iz-btn-secondary inline-flex items-center gap-2 cursor-pointer w-full justify-center ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
             data-testid={`crm-file-${keyName}`}>
        {value || existing ? <FileText className="w-3.5 h-3.5"/> : <Upload className="w-3.5 h-3.5"/>}
        {value ? "Ready to upload" : existing ? "Replace" : "Upload"}
        <input type="file" hidden disabled={disabled}
               accept="application/pdf,image/*,.dwg,.dxf"
               onChange={(e) => onChange(keyName, e.target.files)}/>
      </label>
      {existing?.data_url && !value && (
        <a href={existing.data_url} target="_blank" rel="noreferrer"
           className="mt-1 block text-[10px] text-[#d4af37] hover:underline truncate"
           data-testid={`crm-file-${keyName}-current`}>
          View current
        </a>
      )}
    </div>
  );
}
